import React from 'react'
import Link from 'next/link'
import cn from 'classnames'
import Router from 'next/router'

import { AffiliationEnum } from '@interfaces/AffiliationEnum'
import { BookEnum } from '@interfaces/BookEnum'
import { Button } from '@components/Buttons'
import { TiptapViewer } from '@components/TiptapViewer'
import { UserAvatar } from '@components/UserAvatar'
import { AdminBadgeIcon } from '@components/icons/AdminBadgeIcon'
import { CheckIcon } from '@components/icons/CheckIcon'
import { ThemeEnum } from '@hooks/contexts/ThemeContext/interface'
import { useTheme } from '@hooks/contexts/ThemeContext/ThemeContext'
import { UserMissionEnum } from '@interfaces/UserMissionEnum'
import { FontFamilyEnum } from '@interfaces/FontFamilyEnum'
import { ReadingPageFooterProps } from './interface'
import { PrevNextSection } from './PrevNextSection'

export function ReadingPageFooter({
  messageFormWriter,
  displayName,
  penName,
  penNameCover,
  affiliation,
  isWriter,
  bookId,
  prevId,
  nextId,
  slug,
  bookType,
  mode = UserMissionEnum.WRITER,
  fontFamily,
  isFollowedUser,
  isFollowedPenName,
  isToggleFollowPenNameLoading,
  isToggleFollowUserLoading,
  canFollow,
  isAdmin,
  email,
  isPermanentBan,
  handleFollowPenName,
  handleFollowUser,
}: ReadingPageFooterProps) {
  const {
    theme,
    fontSize,
    lineHeight,
    isLightOrSepiaTheme,
    isNightOrDarkTheme,
    isDarkTheme,
  } = useTheme()

  const prevUrl =
    mode === UserMissionEnum.WRITER
      ? `/my-writing/${bookId}/chapter/${prevId}`
      : mode === UserMissionEnum.READER
      ? `/story/${bookId}/chapter/${prevId}`
      : `/ebook/${bookId}/chapter/${prevId}`
  const nextUrl =
    mode === UserMissionEnum.WRITER
      ? `/my-writing/${bookId}/chapter/${nextId}`
      : mode === UserMissionEnum.READER
      ? `/story/${bookId}/chapter/${nextId}`
      : `/ebook/${bookId}/chapter/${nextId}`

  return (
    <>
      {bookType === BookEnum.MANGA && (
        <PrevNextSection
          prevUrl={prevUrl}
          nextUrl={nextUrl}
          disablePrev={!bookId || !prevId}
          disableNext={!bookId || !nextId}
          isDarkTheme={isDarkTheme}
          isLightOrSepiaTheme={isLightOrSepiaTheme}
        />
      )}
      <hr
        className={cn('tablet-mobile:mt-[40px]', {
          'text-gray-3': isLightOrSepiaTheme,
          'text-white/10': isNightOrDarkTheme,
          'mt-[40px]': bookType === BookEnum.NOVEL,
        })}
      />
      <div
        className={cn(
          'grid py-[50px] dark:text-white gap-y-[50px] mobile:py-[20px]',
          {
            'text-secondary': isLightOrSepiaTheme,
            '!text-gray-16':
              theme === ThemeEnum.DARK &&
              Router.pathname === '/story/[id]/chapter/[chapterId]',
          }
        )}
      >
        {messageFormWriter && (
          <div>
            <div className='text-[24px] font-medium font-mitr'>
              ข้อความจากนักเขียน
            </div>
            <TiptapViewer
              className={cn(
                'message-from-writer text-[18px] font-light mt-[20px]',
                {
                  'is-js-font': [FontFamilyEnum.JS_JINDARA].includes(
                    fontFamily
                  ),
                  'font-14': fontSize === 14,
                  'font-16': fontSize === 16,
                  'font-18': fontSize === 18,
                  'font-20': fontSize === 20,
                  'font-22': fontSize === 22,
                  'font-24': fontSize === 24,
                  'line-height-125': lineHeight === 1.25,
                  'line-height-15': lineHeight === 1.5,
                  'line-height-2': lineHeight === 2,
                  'line-height-25': lineHeight === 2.5,
                  'line-height-3': lineHeight === 3,
                  'font-notoSerif': fontFamily === FontFamilyEnum.NOTO_SERIF,
                  'font-baiJamjuree':
                    fontFamily === FontFamilyEnum.BAI_JAMJUREE,
                  'font-jsJindara': fontFamily === FontFamilyEnum.JS_JINDARA,
                  'font-sarabun': fontFamily === FontFamilyEnum.SARABUN,
                  'font-trirong': fontFamily === FontFamilyEnum.TRIRONG,
                }
              )}
              value={messageFormWriter}
            />
          </div>
        )}
        <div>
          <div className='font-mitr text-[24px] font-medium'>
            ข้อมูลนักเขียน
          </div>
          <div className='flex items-center space-x-[20px] mt-5 mobile:space-x-[10px]'>
            {slug && (
              <UserAvatar
                className={cn(
                  'w-[75px] h-[75px] mobile:min-w-[100px] mobile:w-[100px] mobile:h-[100px] shrink-0',
                  {
                    '!opacity-50':
                      theme === ThemeEnum.DARK &&
                      Router.pathname === '/story/[id]/chapter/[chapterId]',
                  }
                )}
                slug={slug}
                isPermanentBan={isPermanentBan}
                src={penNameCover}
                alt='User avatar'
              />
            )}
            <div className='mobile:w-full'>
              <div className='grid grid-cols-[220px_1fr] gap-x-[20px] items-center mobile:grid-cols-[1fr_103px] mobile:gap-x-[10px]'>
                <div
                  className={cn('flex items-center mobile:block', {
                    'text-secondary-1': [
                      ThemeEnum.LIGHT,
                      ThemeEnum.SEPIA,
                    ].includes(theme),
                    '!text-gray-16':
                      theme === ThemeEnum.DARK &&
                      Router.pathname === '/story/[id]/chapter/[chapterId]',
                  })}
                >
                  <span
                    className={cn(
                      'font-light min-w-max dark:text-gray-12 mobile:text-[12px]',
                      {
                        '!text-gray-16':
                          ThemeEnum.DARK === theme &&
                          Router.pathname === '/story/[id]/chapter/[chapterId]',
                      }
                    )}
                  >
                    {isWriter && affiliation === AffiliationEnum.PUBLISHER
                      ? 'สำนักพิมพ์ :'
                      : 'นักเขียน :'}
                  </span>
                  &nbsp;
                  <Link href={`/profile/${slug}`} prefetch={false}>
                    <a
                      className={cn(
                        'relative font-bold dark:text-white hover:underline break-word line-clamp-1 leading-8',
                        {
                          '!text-gray-16':
                            theme === ThemeEnum.DARK &&
                            Router.pathname ===
                              '/story/[id]/chapter/[chapterId]',
                          'text-secondary': [
                            ThemeEnum.LIGHT,
                            ThemeEnum.SEPIA,
                          ].includes(theme),
                          'pr-[30px]': isAdmin || email === 'admin@mareads.com',
                        }
                      )}
                    >
                      {displayName}
                      {(isAdmin || email === 'admin@mareads.com') && (
                        <AdminBadgeIcon className='absolute top-[5px] right-[5px] w-[21.5px] h-[21px]' />
                      )}
                    </a>
                  </Link>
                </div>
                {canFollow && !isPermanentBan && (
                  <Button
                    type='button'
                    className={cn('border h-[30px] font-medium w-[103px]', {
                      'border-primary': isFollowedUser && isLightOrSepiaTheme,
                      'border-white bg-white dark:bg-transparent dark:border-primary':
                        isFollowedUser && isNightOrDarkTheme,
                      'border-secondary-1 dark:border-dark-10':
                        !isFollowedUser && !isDarkTheme,
                      'border-gray-16 ': !isFollowedUser && isDarkTheme,
                    })}
                    disabled={isToggleFollowUserLoading}
                    onClick={handleFollowUser}
                  >
                    <div
                      className={cn('text-sm flex items-center', {
                        'text-primary': isFollowedUser,
                        'text-secondary-1 dark:text-dark-10':
                          !isFollowedUser && !isDarkTheme,
                        'text-gray-16': !isFollowedUser && isDarkTheme,
                      })}
                    >
                      {isFollowedUser && (
                        <CheckIcon
                          className='mr-[10px]'
                          width='16'
                          height='16'
                        />
                      )}
                      ติดตาม
                    </div>
                  </Button>
                )}
              </div>
              <div className='grid grid-cols-[220px_1fr] gap-x-[20px] items-center mt-[15px] mobile:mt-[10px] mobile:grid-cols-[1fr_103px] mobile:gap-x-[10px]'>
                <div
                  className={cn('flex items-center mobile:block', {
                    'text-secondary-1': [
                      ThemeEnum.LIGHT,
                      ThemeEnum.SEPIA,
                    ].includes(theme),

                    '!text-gray-16':
                      theme === ThemeEnum.DARK &&
                      Router.pathname === '/story/[id]/chapter/[chapterId]',
                  })}
                >
                  <span
                    className={cn(
                      'font-light min-w-max dark:text-gray-12 mobile:text-[12px]',
                      {
                        '!text-gray-16':
                          ThemeEnum.DARK === theme &&
                          Router.pathname === '/story/[id]/chapter/[chapterId]',
                      }
                    )}
                  >
                    นามปากกา :
                  </span>
                  &nbsp;
                  <Link
                    href={`/profile/${slug}?tab=penName&text=${penName}`}
                    prefetch={false}
                  >
                    <a
                      className={cn(
                        'font-bold dark:text-white hover:underline break-word line-clamp-1 leading-8',
                        {
                          '!text-gray-16':
                            theme === ThemeEnum.DARK &&
                            Router.pathname ===
                              '/story/[id]/chapter/[chapterId]',
                          'text-secondary': [
                            ThemeEnum.LIGHT,
                            ThemeEnum.SEPIA,
                          ].includes(theme),
                        }
                      )}
                    >
                      {penName}
                    </a>
                  </Link>
                </div>
                {canFollow && !isPermanentBan && (
                  <Button
                    type='button'
                    className={cn('border h-[30px] font-medium w-[103px]', {
                      'border-primary':
                        isFollowedPenName && isLightOrSepiaTheme,
                      'border-white bg-white dark:bg-transparent dark:border-primary':
                        isFollowedPenName && isNightOrDarkTheme,
                      'border-secondary-1': !isFollowedPenName && !isDarkTheme,
                      'border-gray-16': !isFollowedPenName && isDarkTheme,
                    })}
                    disabled={isToggleFollowPenNameLoading}
                    onClick={handleFollowPenName}
                  >
                    <div
                      className={cn('text-sm flex items-center', {
                        'text-primary': isFollowedPenName,
                        'text-secondary-1': !isFollowedPenName && !isDarkTheme,
                        'text-gray-16': !isFollowedPenName && isDarkTheme,
                      })}
                    >
                      {isFollowedPenName && (
                        <CheckIcon
                          className='mr-[10px]'
                          width='16'
                          height='16'
                        />
                      )}
                      ติดตาม
                    </div>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr
        className={cn({
          'text-gray-3': isLightOrSepiaTheme,
          'text-white/10': isNightOrDarkTheme,
        })}
      />
      <PrevNextSection
        prevUrl={prevUrl}
        nextUrl={nextUrl}
        disablePrev={!bookId || !prevId}
        disableNext={!bookId || !nextId}
        isDarkTheme={isDarkTheme}
        isLightOrSepiaTheme={isLightOrSepiaTheme}
      />
    </>
  )
}
